/* @jsx h */
import { defineComponent, h, computed, provide, Teleport } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import {
  Reservation,
  ReservationRequest,
  ACTIVE_TABLE_STATUSES,
} from "@lemmonhq/types";
import { ReservationsList } from "./ReservationsList";
import { useAuth } from "@app/vue/providers/Auth";
import { formatDateRelative } from "@lemmonhq/orders";
import {
  isConfirmedStatus,
  isSeatedStatus,
  reservationsForTable,
  byStartTime,
} from "../../../utils/reservationStatus";
// Removed useTablesSelectedDayData import

export interface TableReservationsPopupProps {
  isOpen: boolean;
  /** Which list to show — chosen by the Confirmed/Pending/Seated toolbar buttons. */
  activeTab: "confirmed" | "pending" | "seated";
  /** When set, show only this table's reservations (opened by tapping a table
   *  with 2+ bookings) instead of the status-tab list. */
  tableFilter?: { tableId: number; tableName: string } | null;
  selectedDate: Date;
  isToday: boolean;
  nowMs: number;
  onClose: () => void;
  /** Called when a confirmed reservation row is tapped — page picks seat vs assign. */
  onSelectReservation: (reservation: Reservation) => void;
  /** Called when a pending request row is tapped — page enters assign+confirm. */
  onSelectRequest: (request: ReservationRequest) => void;
  /** Called when a row is tapped in per-table mode — opens its detail sheet. */
  onViewReservation?: (reservation: Reservation) => void;
  reservations: Reservation[];
  pendingRequests: ReservationRequest[];
  isLoadingReservations: boolean;
}

export const TableReservationsPopup =
  defineComponent<TableReservationsPopupProps>({
    name: "TableReservationsPopup",
    props: [
      "isOpen",
      "activeTab",
      "tableFilter",
      "selectedDate",
      "isToday",
      "nowMs",
      "onClose",
      "onSelectReservation",
      "onSelectRequest",
      "onViewReservation",
      "reservations",
      "pendingRequests",
      "isLoadingReservations",
    ],
    setup(props) {
      const { t } = useLocalization();
      const { user } = useAuth();
      const timezone = computed(
        () => user.value?.restaurant?.timezone || "UTC",
      );
      provide("timezone", timezone);

      const reservations = computed(() => props.reservations);
      const pendingRequests = computed(() => props.pendingRequests);
      const isLoadingReservations = computed(() => props.isLoadingReservations);

      // Confirmed tab = upcoming reservations for the date not yet seated.
      const confirmedList = computed(() =>
        reservations.value
          .filter((r) => isConfirmedStatus(r.status))
          .sort(byStartTime),
      );

      // Seated tab = parties currently seated (incl. arrived / partially-seated).
      const seatedList = computed(() =>
        reservations.value
          .filter((r) => isSeatedStatus(r.status))
          .sort(byStartTime),
      );

      // Pending tab = pending reservation REQUESTS, normalised to the shape the
      // reservation list renders (requested_time → time, no tables). The row click
      // maps the id back to the original request via requestById.
      const requestById = computed(
        () => new Map(pendingRequests.value.map((req) => [req.id, req])),
      );
      const pendingList = computed<Reservation[]>(() =>
        pendingRequests.value
          .map(
            (req) =>
              ({
                id: req.id,
                time: req.requested_time,
                party_name: req.party_name,
                party_size: req.party_size,
                duration_mins: req.duration_mins,
                table_assignments: [],
                status: "pending",
                notes: req.notes,
              }) as unknown as Reservation,
          )
          .sort(byStartTime),
      );

      // Per-table mode = one table's active bookings (confirmed + seated),
      // time-sorted.
      const tableList = computed(() => {
        const tableId = props.tableFilter?.tableId;
        if (tableId == null) return [];
        return reservationsForTable(
          reservations.value,
          tableId,
          ACTIVE_TABLE_STATUSES,
        );
      });

      const currentList = computed(() =>
        props.tableFilter
          ? tableList.value
          : props.activeTab === "confirmed"
            ? confirmedList.value
            : props.activeTab === "seated"
              ? seatedList.value
              : pendingList.value,
      );

      const handleRowTap = (item: Reservation) => {
        if (props.tableFilter) {
          props.onViewReservation?.(item);
        } else if (props.activeTab === "pending") {
          const req = requestById.value.get(item.id);
          if (req) props.onSelectRequest(req);
        } else {
          props.onSelectReservation(item);
        }
      };

      const dateLabel = computed(() =>
        formatDateRelative(props.selectedDate.toISOString(), timezone.value, {
          today: t("general.date.today"),
          tomorrow: t("general.date.tomorrow"),
        }),
      );

      const categoryLabel = computed(() =>
        props.activeTab === "confirmed"
          ? t("reservations.table_popup.confirmed_tab")
          : props.activeTab === "seated"
            ? t("reservations.table_popup.seated_tab")
            : t("reservations.table_popup.pending_tab"),
      );

      return () =>
        props.isOpen ? (
          <Teleport to="body">
            <div
              class="fixed left-2 bottom-2 z-30 flex flex-col rounded-xl border border-base-300 shadow-2xl overflow-hidden reservation-panel-enter"
              style="top: 124px; width: 440px; max-width: calc(100vw - 1rem); background: oklch(var(--b1));"
            >
              <style>{`
              .reservation-panel-enter { animation: resPanelIn 0.16s ease both; }
              @keyframes resPanelIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
            `}</style>
              {/* Header */}
              <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-base-300">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class={`material-symbols-outlined text-xl ${
                      props.tableFilter
                        ? "text-primary"
                        : props.activeTab === "confirmed"
                          ? "text-success"
                          : props.activeTab === "seated"
                            ? "text-info"
                            : "text-warning"
                    }`}
                  >
                    {props.tableFilter
                      ? "table_restaurant"
                      : props.activeTab === "confirmed"
                        ? "event_available"
                        : props.activeTab === "seated"
                          ? "restaurant"
                          : "table_restaurant"}
                  </span>
                  <span class="text-base font-semibold text-accent truncate">
                    {props.tableFilter
                      ? props.tableFilter.tableName
                      : categoryLabel.value}
                  </span>
                  <span class="text-xs font-medium text-base-content/50 ml-1 whitespace-nowrap">
                    {props.tableFilter
                      ? `· ${currentList.value.length} ${t("reservations.general.reservations")}`
                      : `· ${dateLabel.value}`}
                  </span>
                </div>
                <button
                  class="btn btn-ghost btn-sm btn-circle flex-shrink-0"
                  onClick={props.onClose}
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>

              {/* Contextual hint */}
              <div class="px-4 pt-3 pb-2 border-b border-base-300 text-[11px] text-base-content/45 flex items-center gap-1">
                <span class="material-symbols-outlined text-sm">
                  {props.tableFilter || props.activeTab === "seated"
                    ? "info"
                    : props.activeTab === "confirmed" && props.isToday
                      ? "restaurant"
                      : "table_restaurant"}
                </span>
                {props.tableFilter
                  ? t("reservations.table_popup.hint_view")
                  : props.activeTab === "seated"
                    ? t("reservations.table_popup.hint_seated")
                    : props.activeTab === "confirmed"
                      ? props.isToday
                        ? t("reservations.table_popup.hint_tap_to_seat")
                        : t("reservations.table_popup.hint_tap_to_assign")
                      : t("reservations.table_popup.hint_tap_to_assign")}
              </div>

              {isLoadingReservations.value ? (
                <div class="flex justify-center items-center py-16">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              ) : (
                <div class="overflow-y-auto flex-1 px-4 py-3">
                  <ReservationsList
                    reservations={currentList.value}
                    nowMs={props.nowMs}
                    onEnterDropMode={handleRowTap}
                  />
                </div>
              )}
            </div>
          </Teleport>
        ) : null;
    },
  });
